import { zodSsnValidation } from '@fruitful/fruitils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import React, { useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { KeyboardAvoidingView } from 'react-native';
import {
    BodyMediumRegular,
    ControlledInput,
    Form,
    H5Emphasized,
    isNonMobileWeb,
    Private,
    USE_FORM_MODE_DEFAULTS,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import { formatWithMask, SSN_MASK } from '../../../../common/utils/maskUtils';
import { userSsnFromKycAtom } from '../../../../user/state/UserAtoms';
import type { RunKYCVerifyBody } from '../../../hooks/kyc/useRunKycVerify';
import KycButton from './KycButton';

const ssnSchema = z.object({
    ssn: zodSsnValidation,
});

type SsnSchema = z.infer<typeof ssnSchema>;

interface SsnProps {
    onPressNext: (formData?: Partial<RunKYCVerifyBody>) => void;
    saveValueForNextFlow?: boolean;
}

const Ssn = ({ onPressNext, saveValueForNextFlow = false }: SsnProps) => {
    const [showSsn, setShowSsn] = useState(false);
    const setUserSsn = useSetAtom(userSsnFromKycAtom);

    const {
        register,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<SsnSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(ssnSchema),
        defaultValues: {
            ssn: '',
        },
    });

    const onSubmit: SubmitHandler<SsnSchema> = ({ ssn }) => {
        if (saveValueForNextFlow) {
            setUserSsn(ssn);
        }
        // Keep formatting on ssn for synctera request to be successful
        onPressNext({ ssn });
    };

    return (
        <KeyboardAvoidingView
            style={{ flex: 1 }}
            behavior="padding"
            keyboardVerticalOffset={120}
        >
            <YStack
                flex={1}
                justifyContent="space-between"
                gap={isNonMobileWeb ? 60 : 16}
            >
                <View>
                    <H5Emphasized marginBottom={8}>
                        What&apos;s your Social Security{'\n'}number (or ITIN)?
                    </H5Emphasized>
                    <BodyMediumRegular color="$gray1000" marginBottom={24}>
                        We&apos;re required to collect this to verify your
                        identity.
                    </BodyMediumRegular>
                    <Private>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <YStack gap={12}>
                                <ControlledInput
                                    name="ssn"
                                    type="numeric"
                                    control={control}
                                    register={register}
                                    errors={errors}
                                    secureTextEntry={!showSsn}
                                    enterKeyHint="done"
                                    enablesReturnKeyAutomatically={true}
                                    onSubmitEditing={handleSubmit(onSubmit)}
                                    onFocus={() => {
                                        setShowSsn(true);
                                    }}
                                    onEndEditing={() => {
                                        setShowSsn(false);
                                    }}
                                    formatValueOnChange={(_, value) => {
                                        const { masked } = formatWithMask(
                                            SSN_MASK,
                                            value,
                                        );
                                        return masked;
                                    }}
                                />
                            </YStack>
                        </Form>
                    </Private>
                </View>
                <KycButton
                    onPress={handleSubmit(onSubmit)}
                    disabled={!isValid}
                />
            </YStack>
        </KeyboardAvoidingView>
    );
};

export default Ssn;
